'use client';

import type { onboarding } from '@/models/onboarding';
import { getAuthInstance } from './auth';

/**
 * Onboarding API.
 *
 * Calls this app's own `/api/onboarding/*` route handlers (same origin), which
 * forward to the SYNC/app-api backend server-side. That proxying used to live in
 * the separate auth service; keeping it in-app is what lets the portal run
 * standalone without `web-react-syde-auth`.
 */
const OnboardingApi = {
  getInfo: async () => {
    const instance = getAuthInstance();
    const { data } = await instance.get<onboarding.OperatorInfoResponse>(
      '/api/onboarding/info',
    );
    return data;
  },

  getTerms: async () => {
    const instance = getAuthInstance();
    const { data } = await instance.get<onboarding.TermsResponse>(
      '/api/onboarding/terms',
    );
    return data;
  },

  acceptTerms: async (payload: onboarding.AcceptTermsPayload) => {
    const instance = getAuthInstance();
    const { data } = await instance.post<onboarding.MessageResponse>(
      '/api/onboarding/terms',
      payload,
    );
    return data;
  },

  validateInvite: async (authorizationCode: string) => {
    const instance = getAuthInstance();
    const { data } = await instance.post<onboarding.MessageResponse>(
      '/api/onboarding/validate-invite',
      { authorizationCode },
    );
    return data;
  },

  updateCompanyData: async (payload: onboarding.UpdateCompanyDataPayload) => {
    const instance = getAuthInstance();
    const { data } = await instance.post<onboarding.MessageResponse>(
      '/api/onboarding/account-company',
      payload,
    );
    return data;
  },

  updatePersonalData: async (payload: onboarding.UpdatePersonalDataPayload) => {
    const instance = getAuthInstance();
    const { data } = await instance.put<onboarding.MessageResponse>(
      '/api/onboarding/personal-data',
      payload,
    );
    return data;
  },

  updateAddress: async (payload: onboarding.UpdateAddressPayload) => {
    const instance = getAuthInstance();
    const { data } = await instance.post<onboarding.MessageResponse>(
      '/api/onboarding/address',
      payload,
    );
    return data;
  },

  searchAddressByCep: async (cep: string) => {
    const instance = getAuthInstance();
    const { data } = await instance.get<onboarding.AddressLookup>(
      '/api/onboarding/address',
      { params: { cep } },
    );
    return data;
  },
};

export default OnboardingApi;
